/**
 * The tenant's and owner's side of the application: its client, and who is signed in.
 *
 * **Deliberately separate from `lib/api.ts` and `lib/session.tsx`.** Those speak to `/api/v1` with
 * the agency's cookie; this speaks to `/api/portal` with a bearer token belonging to a tenant. The
 * two credentials are different kinds of thing and must never be interchangeable — the API enforces
 * that at the door (see `apps/api/src/lib/portal.ts`), and keeping the clients apart here means no
 * screen can reach for the wrong one by habit.
 *
 * The token lives in `localStorage` rather than a cookie because a tenant may well be signed into
 * their own agency's app in the same browser — an accountant who also rents — and one cookie name
 * cannot mean two things. It survives a reload, which matters: somebody who opened a link from an
 * email at a bus stop should not have to ask for another one when they get home.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { recaptchaHeader, type RecaptchaAction } from "./recaptcha";

const KEY = "rentorng.portal";
const BASE = "/api/portal";

function storedToken(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    // Private mode throws rather than returning null. A tenant can still use the link they arrived
    // with; they will simply be asked again next time.
    return null;
  }
}

function storeToken(value: string | null): void {
  try {
    if (value) window.localStorage.setItem(KEY, value);
    else window.localStorage.removeItem(KEY);
  } catch {
    /* See above. */
  }
}

export class PortalError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, options: { method?: string; body?: unknown; token?: string | null; action?: RecaptchaAction } = {}): Promise<T> {
  /* Asking for a link is the one thing here anybody can do without already being somebody, so it is
     the one thing that carries a captcha. Everything else needs the session it hands out. */
  const human = options.action ? await recaptchaHeader(options.action) : {};
  const response = await fetch(`${BASE}${path}`, {
    method: options.method ?? "GET",
    headers: {
      ...(options.body ? { "content-type": "application/json" } : {}),
      ...(options.token ? { authorization: `Bearer ${options.token}` } : {}),
      ...human,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const message =
      (payload as { error?: string } | null)?.error ??
      "Something went wrong. Try again, and if it keeps happening tell your agency.";
    throw new PortalError(message, response.status);
  }

  return payload as T;
}

export interface PortalAgency {
  name: string;
  phone: string | null;
  email: string | null;
  currency: string;
  timezone: string;
  /**
   * Where the agency's own mark is served, or null.
   *
   * A root-relative path written by `POST /settings/logo`, the same one the agency's own screens
   * and the public pay page read. Optional in the type because a payload from before the field
   * existed carries none, and the honest reading of that is "no mark" rather than a broken square.
   */
  logoUrl?: string | null;
}

export interface PortalMe {
  name: string;
  roles: string[];
  /**
   * Whether this person sells for the agency, and therefore whether to offer them the commission
   * screen at all.
   *
   * The server reads it from the `marketers` table rather than from `parties.roles` — the role is
   * what an office ticked on a form, the row is the record of who actually sells here, and
   * `/commission` decides the same way. So this is the one thing worth asking: a screen that
   * instead fetched `/commission` and read its 404 as "no such feature" would be treating a
   * refusal as a switch, which is the reading that turns any future outage into a missing menu.
   *
   * Optional in the type because a session held from before the field existed will not carry it,
   * and the honest answer for a payload that never mentioned it is "not offered".
   */
  marketer?: boolean;
  agency: PortalAgency | null;
}

interface PortalState {
  loading: boolean;
  me: PortalMe | null;
  token: string | null;
  /** Exchange a link's token for a session. Returns nothing; throws with a readable message. */
  enter: (linkToken: string) => Promise<void>;
  /**
   * Hold a session token that was issued some other way than a link.
   *
   * `POST /portal/sign-in` answers with exactly the token `/enter` does — the same session, the
   * same ninety days, the same scope — so signing in with a password needs somewhere to put it and
   * nothing else. Kept separate from `enter` because that one redeems a single-use link and this
   * one must not: calling `enter` with a session token would spend a link nobody has.
   */
  accept: (sessionToken: string) => void;
  signOut: () => Promise<void>;
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body: unknown, action?: RecaptchaAction) => Promise<T>;
}

const Context = createContext<PortalState | null>(null);

export function PortalProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => storedToken());
  const [me, setMe] = useState<PortalMe | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setMe(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    request<PortalMe>("/me", { token })
      .then((value) => {
        if (!cancelled) setMe(value);
      })
      .catch(() => {
        /* An expired or revoked token is not an error worth showing — it is simply somebody who
           needs a new link, and the sign-in screen says so. Clearing it here stops every subsequent
           screen retrying with a credential that is known to be dead. */
        if (cancelled) return;
        storeToken(null);
        setToken(null);
        setMe(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = useCallback((sessionToken: string) => {
    storeToken(sessionToken);
    setToken(sessionToken);
  }, []);

  const enter = useCallback(
    async (linkToken: string) => {
      const result = await request<{ token: string }>("/enter", { method: "POST", body: { token: linkToken } });
      accept(result.token);
    },
    [accept],
  );

  const signOut = useCallback(async () => {
    // Told to the server as well as forgotten here, so a session ended on a shared machine is
    // actually dead rather than merely out of sight.
    await request("/sign-out", { method: "POST", token }).catch(() => undefined);
    storeToken(null);
    setToken(null);
    setMe(null);
  }, [token]);

  const value = useMemo<PortalState>(
    () => ({
      loading,
      me,
      token,
      enter,
      accept,
      signOut,
      get: <T,>(path: string) => request<T>(path, { token }),
      post: <T,>(path: string, body: unknown, action?: RecaptchaAction) => request<T>(path, { method: "POST", body, token, action }),
    }),
    [loading, me, token, enter, accept, signOut],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function usePortal(): PortalState {
  const value = useContext(Context);
  if (!value) throw new Error("usePortal must be used inside PortalProvider");
  return value;
}
